import React from 'react';
import type { GetStaticProps, NextPage } from 'next';
import moment from 'moment';
import { Twitter, Facebook, Pinterest } from 'react-social-sharing';
import { getBlogBySlug, getWpUserById, getWPBlogs, getBlogArticles } from 'day8-react/build/content';
import {
  ComponentVerticalSpacing,
  ComponentWrapper,
  BlogItemWrapper,
  Avatar,
  BlogPost
} from 'day8-react/build/new-components';
import { formattingHelpers } from 'day8-react';
import PageLayout from '../../../../layouts/page';
import { sanitizeWpResponse } from '../../../../helpers/wp';
import { WpPageType } from '../../../../globalTypes';

type BlogItem = {
  post: WpPageType
  relatedPosts?: {
    posts: WpPageType[]
  }
  user: {
    name: string
    avatar_urls: string[]
  }
}

const BlogItem: NextPage<BlogItem> = ({
  post,
  user,
  relatedPosts
}) => {
  const title = post.title?.rendered;
  const relatedPostsLarge = !!relatedPosts && relatedPosts.posts.length > 1;
  const relatedPostsItems = !!relatedPosts && relatedPosts.posts.map(relatedPost => <BlogPost key={relatedPost.id} post={relatedPost} />);
  return (
    <PageLayout page={post} hiddenCurrency>
      <ComponentVerticalSpacing>
        <ComponentWrapper>
          <BlogItemWrapper>
            <header>
              <h1>{title}</h1>
            </header>
            {user && (
              <div className="flex margin-bottom-lg">
                <div className="left margin-right-sm">
                  <Avatar
                    image={{
                      url: user.avatar_urls[96],
                      alt: user.name
                    }}
                    initials={formattingHelpers.getInitialsFromName(
                      user.name && user.name.split(' ')[0],
                      user.name && user.name.split(' ')[1]
                    )}
                  />
                </div>
                <div className="right text-component">
                  <h4 className="d8-u-font-size-base" style={{ margin: 0 }}>
                    By {user.name}
                  </h4>
                  <p className="d8-u-color-contrast-high" style={{ margin: 0 }}>Posted on {moment(post.date).format('Do MMMM YYYY')}</p>
                </div>
              </div>
            )}
            {post.content?.rendered && (
              <div className="d8-blog-content" dangerouslySetInnerHTML={{ __html: post.content.rendered }} />
            )}
            <div className="clearfix" />
            <h2 className="text-center margin-y-xl">Share this post</h2>
            <div className="flex justify-center">
              <Twitter
                message="Check out"
                link={post.link}
              />
              <Facebook
                message="Check out"
                link={post.link}
              />
              <Pinterest
                message="Check out"
                link={post.link}
              />
            </div>
          </BlogItemWrapper>
        </ComponentWrapper>
      </ComponentVerticalSpacing>
      {relatedPosts !== null && (
        <ComponentVerticalSpacing>
          <ComponentWrapper className={!relatedPostsLarge && 'max-width-xs'}>
            <h2 className="text-center margin-bottom-lg">Related posts</h2>
            <div className="d8-l-auto-grid">{relatedPostsItems}</div>
          </ComponentWrapper>
        </ComponentVerticalSpacing>
      )}
    </PageLayout>
  );
};
type PostResponse = {
  pagesCount: number
}

export async function getStaticPaths() {
  const perPage = 12;
  const postsResponse: PostResponse = await getBlogArticles(1, null, undefined, perPage);
  const pagesCount = Math.floor(postsResponse.pagesCount) > 2 ? 2 : Math.floor(postsResponse.pagesCount);
  const paths = await Promise.all(Array.from(Array(pagesCount).keys()).map(async (i): Promise<object> => {
    const postsResponse = await getBlogArticles(i + 1, null, undefined, perPage);
    return postsResponse.posts.map((post: { date: Date, slug: string }) => {
      const date = new Date(post.date);
      const month = date.getMonth() + 1 > 9 ? `${date.getMonth() + 1}` : `0${date.getMonth() + 1}`; // January gives 0
      return ({
        params: {
          slug: post.slug,
          month,
          year: `${date.getFullYear()}`
        }
      });
    });
  }));
  return {
    paths: paths.flat(),
    fallback: 'blocking'
  };
}
export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const post = await getBlogBySlug(params?.slug);
    const user = post?.author ? await getWpUserById(post?.author) : null;
    const relatedPosts = await getWPBlogs(`posts?per_page=4&tags=${post.tags[0]}&exclude=${post.id}`);
    if (!post) {
      return { notFound: true };
    }
    return {
      props: {
        post: sanitizeWpResponse(post),
        user,
        relatedPosts
      }
    };
  } catch (e) {
    return {
      notFound: true
    };
  }
};
export default BlogItem;